import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Bot, Clapperboard, KeyRound, RefreshCw, MapPinned } from 'lucide-react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { BACKEND_URL, reportError } from './adminApi';

/**
 * Admin card: choose the text model used for Ptitconseils and control the automatic weekly video.
 */
export function AdminAiSettingsCard({ adminKey, refreshToken = 0 }) {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/ai-settings?key=${adminKey}`);
      setSettings(res.data);
    } catch (err) {
      reportError('paramètres IA', err, () => toast.error('Impossible de charger les paramètres IA'));
    }
  }, [adminKey]);

  useEffect(() => { load(); }, [load, refreshToken]);

  const save = async (patch, successMessage) => {
    setSaving(true);
    try {
      const res = await axios.put(`${BACKEND_URL}/api/admin/ai-settings?key=${adminKey}`, patch);
      setSettings(res.data);
      toast.success(successMessage);
    } catch (err) {
      reportError('enregistrement paramètres IA', err, () => toast.error(err?.response?.data?.detail || 'Enregistrement impossible'));
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return (
      <Card className="p-5 flex items-center gap-2 text-sm text-muted-foreground" data-testid="ai-settings-loading">
        <RefreshCw className="h-4 w-4 animate-spin" /> Chargement des paramètres IA…
      </Card>
    );
  }

  const currentModel = settings.text_models.find((m) => m.id === settings.text_model);

  return (
    <Card className="p-5 sm:p-6" data-testid="ai-settings-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-[hsl(var(--brand-mint))] flex items-center justify-center"><Bot className="h-5 w-5 text-[hsl(var(--primary))]" /></div>
        <div>
          <h3 className="font-['Space_Grotesk'] font-semibold">Moteurs IA des Ptitconseils</h3>
          <p className="text-xs text-muted-foreground">Choisissez l'IA qui rédige vos astuces/tutos et pilotez la vidéo hebdomadaire.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Text model */}
        <div className="space-y-2">
          <label className="text-xs font-medium block">Modèle de rédaction (texte)</label>
          <Select value={settings.text_model} onValueChange={(v) => save({ text_model: v }, 'Modèle texte enregistré')} disabled={saving}>
            <SelectTrigger data-testid="ai-text-model-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {settings.text_models.map((m) => (
                <SelectItem key={m.id} value={m.id} data-testid={`ai-text-model-option-${m.id}`}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {currentModel && <p className="text-[11px] text-muted-foreground" data-testid="ai-text-model-note">{currentModel.note}</p>}
          <div className="flex flex-wrap gap-1.5 pt-1">
            <Badge variant="secondary" className="text-[10px]" data-testid="ai-universal-key-badge"><KeyRound className="h-3 w-3 mr-1" />Clé universelle {settings.universal_key_configured ? 'active' : 'absente'}</Badge>
            <Badge variant="secondary" className="text-[10px]" data-testid="ai-google-key-badge"><KeyRound className="h-3 w-3 mr-1" />Clé Google {settings.google_key_configured ? 'active (priorité Gemini)' : 'absente'}</Badge>
            <Badge variant="secondary" className="text-[10px]" data-testid="ai-video-billing-badge"><KeyRound className="h-3 w-3 mr-1" />Vidéo : {settings.video_billing === 'fal-direct' ? 'compte fal.ai' : 'clé universelle'}</Badge>
          </div>
        </div>

        {/* Seasonal city content automation */}
        <div className="space-y-2 rounded-xl border border-border p-4 bg-[hsl(var(--secondary))]/40 lg:col-span-2">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-medium flex items-center gap-1.5"><MapPinned className="h-4 w-4 text-[hsl(var(--primary))]" /> Textes de saison des pages villes</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">Rédaction automatique, à chaque saison, d'un gabarit par type de page (4 appels IA par saison) ensuite adapté à chaque ville ou village : coût quasi nul.</p>
            </div>
            <Switch checked={!!settings.auto_city_content} onCheckedChange={(v) => save({ auto_city_content: v }, v ? 'Rédaction des pages villes activée' : 'Rédaction des pages villes désactivée')} disabled={saving} data-testid="ai-auto-city-content-switch" />
          </div>
        </div>

        {/* Video automation */}
        <div className="space-y-3 rounded-xl border border-border p-4 bg-[hsl(var(--secondary))]/40">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-medium flex items-center gap-1.5"><Clapperboard className="h-4 w-4 text-[hsl(var(--primary))]" /> Vidéo automatique du tuto hebdo</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">Chaque nouveau tutoriel reçoit un clip de 5 s (≈ {settings.video_price_usd} $ / vidéo, Wan 2.2 via fal.ai).</p>
            </div>
            <Switch checked={!!settings.auto_video_tutos} onCheckedChange={(v) => save({ auto_video_tutos: v }, v ? 'Vidéo hebdomadaire activée' : 'Vidéo hebdomadaire désactivée')} disabled={saving} data-testid="ai-auto-video-switch" />
          </div>
          <div>
            <label className="text-xs font-medium block mb-1">Mode de la vidéo automatique</label>
            <Select value={settings.auto_video_mode} onValueChange={(v) => save({ auto_video_mode: v }, 'Mode vidéo enregistré')} disabled={saving || !settings.auto_video_tutos}>
              <SelectTrigger data-testid="ai-auto-video-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {settings.video_modes.map((m) => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </Card>
  );
}
